import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import CheckRounded from '@mui/icons-material/CheckRounded';
import type { BrandState } from '@/marketplace/brands';
import { contrastRatio } from '@/theme/palette';
import { useTenant } from '@/tenant/useTenant';
import { useTicker } from '../motion';

const pop = keyframes`
  0% { transform: scale(0.2); opacity: 0; }
  60% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;
const fill = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

const OTHER_APPS = [
  '#34A853',
  '#4285F4',
  '#EA4335',
  '#FBBC05',
  '#7E57C2',
  '#26A69A',
  '#EC407A',
  '#8D6E63',
];

function readableOn(color: string) {
  return contrastRatio('#FFFFFF', color) >= contrastRatio('#111111', color) ? '#FFFFFF' : '#111111';
}

function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (
    words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? 'V').slice(0, 2)
  ).toUpperCase();
}

/**
 * A phone home screen where the visitor's own app gets installed: the paid
 * "App com a sua marca" service, explained without words.
 */
export function OwnAppShowcase({ brand }: { brand: BrandState }) {
  // 0: store listing downloading · 1: icon lands on the home screen · 2: app open
  const step = useTicker(3, 2600);
  const { config } = useTenant();
  const platformName = config.platformName;
  const name = brand.name.trim() || 'Sua Marca';
  const on = readableOn(brand.color);
  const icon = (size: number, animate = false) => (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: `${Math.round(size * 0.28)}px`,
        bgcolor: brand.color,
        color: on,
        display: 'grid',
        placeItems: 'center',
        fontWeight: 800,
        fontSize: size * 0.36,
        boxShadow: `0 8px 18px -8px ${brand.color}`,
        animation: animate ? `${pop} 600ms ease-out both` : undefined,
      }}
    >
      {initials(name)}
    </Box>
  );

  return (
    <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'center' }}>
      <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          role="img"
          aria-label={`Celular com o app ${name} instalado`}
          sx={{
            width: 260,
            height: 520,
            borderRadius: '38px',
            p: '12px',
            bgcolor: '#111418',
            boxShadow: '0 40px 80px -40px rgba(0,0,0,.55)',
          }}
        >
          <Box
            sx={{
              height: '100%',
              borderRadius: '28px',
              overflow: 'hidden',
              position: 'relative',
              background: 'linear-gradient(160deg, #dfe7f5, #f3e7f1)',
            }}
          >
            {step === 0 && (
              <Stack spacing={1.5} sx={{ p: 2.5, pt: 5, bgcolor: '#fff', height: '100%' }}>
                <Typography variant="caption" color="#5f6368">
                  Google Play
                </Typography>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  {icon(56)}
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#202124' }}>{name}</Typography>
                    <Typography variant="caption" color="#1a73e8">
                      {name}
                    </Typography>
                  </Box>
                </Stack>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#e8eaed', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      bgcolor: '#1a73e8',
                      animation: `${fill} 2.2s ease-in-out both`,
                    }}
                  />
                </Box>
                <Typography variant="caption" color="#5f6368">
                  Instalando…
                </Typography>
                <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                  {[0.9, 0.6, 0.35].map((alpha) => (
                    <Box
                      key={alpha}
                      sx={{
                        flex: 1,
                        height: 150,
                        borderRadius: 2,
                        bgcolor: brand.color,
                        opacity: alpha,
                      }}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="#5f6368">
                  Treinos, dieta e evolução com {name}.
                </Typography>
              </Stack>
            )}
            {step === 1 && (
              <Box
                sx={{
                  p: 2.5,
                  pt: 6,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  rowGap: 2.5,
                  justifyItems: 'center',
                }}
              >
                {OTHER_APPS.map((color) => (
                  <Box key={color} sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '13px',
                        bgcolor: color,
                        opacity: 0.85,
                      }}
                    />
                    <Box
                      sx={{
                        mt: 0.5,
                        width: 30,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'rgba(0,0,0,.18)',
                        mx: 'auto',
                      }}
                    />
                  </Box>
                ))}
                <Box sx={{ textAlign: 'center' }}>
                  {icon(44, true)}
                  <Typography sx={{ fontSize: 9, mt: 0.5, color: '#202124', maxWidth: 56 }} noWrap>
                    {name}
                  </Typography>
                </Box>
              </Box>
            )}
            {step === 2 && (
              <Box sx={{ height: '100%', bgcolor: '#fff' }}>
                <Box sx={{ bgcolor: brand.color, color: on, p: 2.5, pt: 5 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {icon(32)}
                    <Typography sx={{ fontWeight: 800 }}>{name}</Typography>
                  </Stack>
                  <Typography sx={{ mt: 2, fontSize: 13, opacity: 0.9 }}>Treino de hoje</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: 20 }}>
                    Inferiores · 45 min
                  </Typography>
                </Box>
                <Stack spacing={1} sx={{ p: 2 }}>
                  {['Agachamento 4×10', 'Leg press 4×12', 'Stiff 3×12'].map((row) => (
                    <Stack
                      key={row}
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', p: 1, borderRadius: 2, bgcolor: '#f5f5f7' }}
                    >
                      <CheckRounded sx={{ fontSize: 18, color: brand.color }} />
                      <Typography sx={{ fontSize: 13, color: '#202124' }}>{row}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack spacing={2}>
          <Chip
            label="Serviço à parte"
            color="primary"
            variant="soft"
            sx={{ alignSelf: 'flex-start' }}
          />
          <Typography variant="h4" component="h3">
            {name} na Google Play, com o seu ícone
          </Typography>
          <Typography color="text.secondary">
            Todo aluno já pode usar o app <b>{platformName}</b> e entrar no seu espaço. Se você quer
            ir além, publicamos um app só seu: seu nome na loja, seu ícone na tela do celular e as
            suas cores do começo ao fim.
          </Typography>
          <Stack spacing={1}>
            {[
              'Publicado na sua conta de desenvolvedor da Google Play (o app é seu)',
              `Mesmos recursos do ${platformName}, sempre atualizados por nós`,
              'Página da loja, ícone e imagens feitos pela nossa equipe',
            ].map((item) => (
              <Stack key={item} direction="row" spacing={1}>
                <CheckRounded color="primary" sx={{ fontSize: 20, mt: 0.2 }} />
                <Typography>{item}</Typography>
              </Stack>
            ))}
          </Stack>
          <Typography>
            <b>R$ 390</b> de implantação + <b>R$ 59,90/mês</b>, junto com qualquer plano pago.
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
}
